// src/utils/request.ts
import axios from 'axios'
import { apiErrorMessage } from '@/utils/apiErrorMessage'
import type { AxiosRequestConfig, InternalAxiosRequestConfig, AxiosResponse } from 'axios'
import {
  clearStoredSession,
  notifySessionExpired,
  refreshAccessToken,
  refreshAccessTokenResult,
  shouldRefreshToken,
} from '@/utils/authSession'

const configuredTimeout = Number(import.meta.env.VITE_API_TIMEOUT_MS)
const apiTimeout =
  Number.isFinite(configuredTimeout) && configuredTimeout > 0 ? configuredTimeout : 30000

export type ApiRequestConfig = AxiosRequestConfig & {
  skipErrorAlert?: boolean
}

type RequestConfigWithTiming = InternalAxiosRequestConfig & {
  skipErrorAlert?: boolean
  requestStartedAt?: number
  _authRetry?: boolean
}

let lastAlertMessage = ''
let lastAlertAt = 0

const showAlertOnce = (message: string) => {
  const now = Date.now()
  if (message === lastAlertMessage && now - lastAlertAt < 3000) return

  lastAlertMessage = message
  lastAlertAt = now
  alert(message)
}

// 1. 建立 Axios 實例
const service = axios.create({
  // 自動讀取環境變數 VITE_API_BASE_URL
  baseURL: import.meta.env.VITE_API_BASE_URL,
  timeout: apiTimeout,
})

// 2. 請求攔截器 (Request Interceptor) —— 自動蓋上 Token 簽證章
service.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    ;(config as RequestConfigWithTiming).requestStartedAt = Date.now()

    let token = localStorage.getItem('token')
    if (token && shouldRefreshToken(token) && document.visibilityState === 'visible') {
      const refreshedToken = await refreshAccessToken()
      if (refreshedToken) token = refreshedToken
    }

    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`
    }

    return config
  },
  (error) => {
    return Promise.reject(error)
  },
)

// 3. 回應攔截器 (Response Interceptor) —— 統一處理後端報錯
service.interceptors.response.use(
  (response: AxiosResponse) => {
    // 如果 HTTP 狀態碼為 200，直接回傳資料主體
    return response.data
  },
  async (error) => {
    const config = error.config as RequestConfigWithTiming | undefined
    const elapsedMs = config?.requestStartedAt ? Date.now() - config.requestStartedAt : undefined
    const requestUrl = `${config?.baseURL ?? ''}${config?.url ?? ''}`

    console.error('API 請求失敗:', {
      url: requestUrl,
      method: config?.method?.toUpperCase(),
      elapsedMs,
      code: error.code,
      status: error.response?.status,
      message: error.message,
    })

    if (axios.isCancel(error) || error.code === 'ERR_CANCELED') {
      return Promise.reject(error)
    }

    // 由呼叫端顯示專屬錯誤訊息，401 仍沿用登入更新流程。
    if (config?.skipErrorAlert && error.response?.status !== 401) {
      return Promise.reject(error)
    }

    // 這裡專門捕捉標準的 HTTP 錯誤狀態碼 (例如 401, 403, 500)
    if (error.response) {
      const status = error.response.status

      switch (status) {
        case 400:
          showAlertOnce(
            apiErrorMessage(error.response.data) || '請求資料有誤，請確認輸入內容後重試。',
          )
          break

        case 401:
          if (config && !config._authRetry && document.visibilityState === 'visible') {
            config._authRetry = true
            const refreshResult = await refreshAccessTokenResult()
            if (refreshResult.token) {
              config.headers['Authorization'] = `Bearer ${refreshResult.token}`
              return service(config)
            }
            if (!refreshResult.sessionExpired) return Promise.reject(error)
          }
          clearStoredSession()
          notifySessionExpired()
          return Promise.reject(error)

        case 403:
          // 🔴 權限不足 (例如跨營業所竄改案件)
          showAlertOnce('❌ 您的帳號無權限執行此操作。')
          break

        case 404:
          showAlertOnce('🔍 找不到請求的資源。')
          break

        case 500:
          showAlertOnce('🚨 伺服器發生未預期錯誤，請稍後再試。')
          break

        case 408:
        case 504:
          showAlertOnce('⏱️ 伺服器處理逾時，請稍後再試。')
          break

        default:
          showAlertOnce(`⚠️ 系統發生錯誤 (${status})`)
      }
    } else if (error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT') {
      showAlertOnce(`⏱️ 伺服器回應超過 ${Math.round(apiTimeout / 1000)} 秒，請稍後再試。`)
    } else if (error.code === 'ERR_NETWORK') {
      showAlertOnce('🌐 無法連線到伺服器，請檢查網路連線後再試。')
    } else {
      showAlertOnce('⚠️ 請求未完成，請稍後再試。')
    }

    return Promise.reject(error)
  },
)

export default service
